/**
 * <product-form>
 * Wraps a <form action="/cart/add" method="post"> and submits via fetch.
 * Listens for variant:changed to keep the hidden id and price in sync.
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();
    this._submitting = false;
    this._defaultLabel = 'AÑADIR AL CARRITO';
    this._loadingLabel = 'AÑADIENDO...';
    this._onSubmit = this._onSubmit.bind(this);
    this._onVariantChanged = this._onVariantChanged.bind(this);
  }

  connectedCallback() {
    this.$form = this.querySelector('form');
    if (!this.$form) return;

    this.$submit = this.$form.querySelector('[type="submit"]');
    this.$idInput = this.$form.querySelector('[name="id"]');
    this.$error = this.querySelector('[data-form-error]') || this._makeErrorNode();

    if (this.$submit) {
      const txt = this.$submit.textContent && this.$submit.textContent.trim();
      if (txt) this._defaultLabel = txt;
    }

    this.$form.addEventListener('submit', this._onSubmit);
    document.addEventListener('variant:changed', this._onVariantChanged);
  }

  disconnectedCallback() {
    if (this.$form) this.$form.removeEventListener('submit', this._onSubmit);
    document.removeEventListener('variant:changed', this._onVariantChanged);
  }

  _makeErrorNode() {
    const div = document.createElement('div');
    div.className = 'product-form__error';
    div.setAttribute('role', 'alert');
    div.setAttribute('data-form-error', '');
    div.hidden = true;
    if (this.$submit && this.$submit.parentNode) {
      this.$submit.parentNode.insertBefore(div, this.$submit.nextSibling);
    } else {
      this.appendChild(div);
    }
    return div;
  }

  async _onSubmit(e) {
    e.preventDefault();
    if (this._submitting || !this.$form) return;

    this._setSubmitting(true);
    this._clearError();

    try {
      const formData = new FormData(this.$form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg = (data && (data.description || data.message)) || 'No se pudo añadir el producto.';
        this._showError(msg);
        return;
      }

      // Notify the rest of the theme.
      document.dispatchEvent(new CustomEvent('cart:added', {
        bubbles: true,
        detail: data
      }));

      // Programmatically open the cart drawer.
      this._triggerOpenCart();
    } catch (err) {
      this._showError('Error de conexión. Intenta de nuevo.');
    } finally {
      this._setSubmitting(false);
    }
  }

  _onVariantChanged(e) {
    const variant = e && e.detail && e.detail.variant;
    if (!variant) return;

    if (this.$idInput) this.$idInput.value = String(variant.id);

    // Update price displays inside this form (or globally if marked).
    const priceTargets = document.querySelectorAll('[data-variant-price]');
    priceTargets.forEach((el) => {
      el.textContent = this._formatMoney(variant.price);
    });

    // Update availability state on the submit button.
    if (this.$submit) {
      if (variant.available === false) {
        this.$submit.disabled = true;
        this.$submit.textContent = 'SIN STOCK';
      } else if (!this._submitting) {
        this.$submit.disabled = false;
        this.$submit.textContent = this._defaultLabel;
      }
    }
  }

  _setSubmitting(on) {
    this._submitting = on;
    if (!this.$submit) return;
    this.$submit.disabled = on;
    this.$submit.setAttribute('aria-busy', on ? 'true' : 'false');
    this.$submit.textContent = on ? this._loadingLabel : this._defaultLabel;
  }

  _showError(msg) {
    if (!this.$error) return;
    this.$error.textContent = msg;
    this.$error.hidden = false;
  }

  _clearError() {
    if (!this.$error) return;
    this.$error.textContent = '';
    this.$error.hidden = true;
  }

  _triggerOpenCart() {
    const trigger = document.querySelector('[data-action="open-cart"]');
    if (trigger && typeof trigger.click === 'function') {
      trigger.click();
    } else {
      // Fallback: dispatch a synthetic click event so cart-drawer's listener sees it.
      const evt = new MouseEvent('click', { bubbles: true, cancelable: true });
      const target = document.querySelector('cart-drawer');
      if (target) target.dispatchEvent(evt);
    }
  }

  _formatMoney(cents) {
    const n = (Number(cents) || 0) / 100;
    return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

customElements.define('product-form', ProductForm);
